import React, { useState } from 'react';
import type { PageContent } from '../types';
import { produce } from "immer";
import { RichTextEditor } from './RichTextEditor';

interface AdminControlsProps {
  pageId: string;
  pageContent: PageContent;
  onContentChange: (newContent: PageContent) => void;
  onSave: () => void;
}

export const AdminControls: React.FC<AdminControlsProps> = ({ pageId, pageContent, onContentChange, onSave }) => {
  const [isPreviewing, setIsPreviewing] = useState(false);
  
  const handleContentFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onContentChange({
      ...pageContent,
      [name]: value,
    });
  };

  const handleBodyChange = (value: string) => {
    onContentChange({
      ...pageContent,
      body: value,
    });
  };

  const handleBodyBelowChange = (value: string) => {
    onContentChange({
      ...pageContent,
      bodyBelow: value,
    });
  };
  
  const handleWelcomeMessageChange = (value: string) => {
    onContentChange({
      ...pageContent,
      welcomeMessage: value,
    });
  };
  
  const handleSeoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, type } = e.target;
    const value = type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    
    onContentChange({
      ...pageContent,
      seo: {
        ...pageContent.seo,
        [name]: value,
      },
    });
  };

  const handleFaqChange = (index: number, field: 'question' | 'answer', value: string) => {
    const newContent = produce(pageContent, draft => {
      if (draft.faqs) {
        draft.faqs[index][field] = value;
      }
    });
    onContentChange(newContent);
  };

  const addFaq = () => {
    const newContent = produce(pageContent, draft => {
      if (!draft.faqs) {
        draft.faqs = [];
      }
      draft.faqs.push({ question: 'New Question', answer: 'New Answer' });
    });
    onContentChange(newContent);
  };

  const removeFaq = (indexToRemove: number) => {
    const newContent = produce(pageContent, draft => {
      if (draft.faqs) {
        draft.faqs.splice(indexToRemove, 1);
      }
    });
    onContentChange(newContent);
  };

  const hasBody = pageContent.body !== undefined;
  const hasWelcomeMessage = pageContent.welcomeMessage !== undefined;
  const hasFaqs = pageContent.faqs !== undefined;

  return (
    <div className="my-8 p-6 bg-indigo-50 border border-indigo-200 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-indigo-700">Admin Controls: Content & SEO</h3>
        <button
          onClick={() => setIsPreviewing(!isPreviewing)}
          className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-lg shadow-sm transition-colors duration-300 text-sm"
        >
          {isPreviewing ? 'Exit Preview' : 'Preview Changes'}
        </button>
      </div>
      
      {!isPreviewing && (
        <>
          <div className="space-y-4">
            {/* Page Content Fields */}
            <h4 className="text-md font-bold text-indigo-600">Page Content</h4>
            <div>
              <label htmlFor="h1" className="block text-sm font-medium text-[#6B7280] mb-1">H1 Title</label>
              <input
                type="text"
                id="h1"
                name="h1"
                value={pageContent.h1 || ''}
                onChange={handleContentFieldChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow"
              />
            </div>

            {hasWelcomeMessage ? (
              <div>
                <label htmlFor="welcomeMessage" className="block text-sm font-medium text-[#6B7280] mb-1">Welcome Message (Body)</label>
                <div className="prose max-w-none ck-editor-container">
                  <RichTextEditor
                    value={pageContent.welcomeMessage || ''}
                    onChange={handleWelcomeMessageChange}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Placeholders available: <code>{'{userName}'}</code> and <code>{'{userEmail}'}</code>.</p>
              </div>
            ) : hasBody ? (
              <>
                <div>
                  <label htmlFor="body" className="block text-sm font-medium text-[#6B7280] mb-1">Body Text / Content (Above Tool)</label>
                  <div className="prose max-w-none ck-editor-container">
                    <RichTextEditor
                        value={pageContent.body || ''}
                        onChange={handleBodyChange}
                      />
                  </div>
                </div>
                {pageId === 'brandAudit' && (
                  <div className="mt-4">
                    <label htmlFor="bodyBelow" className="block text-sm font-medium text-[#6B7280] mb-1">Body Text / Content (Below Tool)</label>
                    <div className="prose max-w-none ck-editor-container">
                      <RichTextEditor
                          value={pageContent.bodyBelow || ''}
                          onChange={handleBodyBelowChange}
                        />
                    </div>
                  </div>
                )}
              </>
            ) : null}

            {/* FAQ Fields (only for Help page) */}
            {hasFaqs && (
              <>
                <h4 className="text-md font-bold text-indigo-600 pt-4 border-t border-indigo-200">FAQs</h4>
                <div className="space-y-4">
                  {(pageContent.faqs || []).map((faq, index) => (
                    <div key={index} className="p-4 bg-gray-100 rounded-lg border border-gray-300 relative space-y-2">
                      <button 
                        onClick={() => removeFaq(index)} 
                        className="absolute top-2 right-2 text-sm text-red-600 hover:text-red-800 font-medium"
                        aria-label={`Remove FAQ item ${index + 1}`}
                      >
                        &times; Remove
                      </button>
                      <div>
                        <label htmlFor={`faq-q-${index}`} className="block text-sm font-medium text-[#6B7280] mb-1">Question {index + 1}</label>
                        <input
                          id={`faq-q-${index}`}
                          type="text"
                          value={faq.question}
                          onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow"
                        />
                      </div>
                      <div>
                        <label htmlFor={`faq-a-${index}`} className="block text-sm font-medium text-[#6B7280] mb-1">Answer {index + 1}</label>
                        <div className="prose max-w-none ck-editor-container">
                            <RichTextEditor
                                value={faq.answer}
                                onChange={(value) => handleFaqChange(index, 'answer', value)}
                            />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <button 
                    onClick={addFaq}
                    className="px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold rounded-lg shadow-sm transition-colors duration-300 text-sm"
                  >
                    + Add FAQ
                  </button>
                </div>
              </>
            )}

            {/* SEO Fields */}
            <h4 className="text-md font-bold text-indigo-600 pt-4 border-t border-indigo-200">SEO Placeholders</h4>
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-[#6B7280] mb-1">Page Title (&lt;title&gt;)</label>
              <input
                type="text"
                id="title"
                name="title"
                value={pageContent.seo.title}
                onChange={handleSeoChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow"
              />
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-[#6B7280] mb-1">Meta Description</label>
              <input
                type="text"
                id="description"
                name="description"
                value={pageContent.seo.description}
                onChange={handleSeoChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow"
              />
            </div>
            <div>
              <label htmlFor="canonical" className="block text-sm font-medium text-[#6B7280] mb-1">Canonical URL</label>
              <input
                type="text"
                id="canonical"
                name="canonical"
                value={pageContent.seo.canonical}
                onChange={handleSeoChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow"
              />
            </div>
            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-[#6B7280] mb-1">URL Slug</label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={pageContent.seo.slug}
                onChange={handleSeoChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow"
              />
            </div>
            <div>
              <label htmlFor="customHead" className="block text-sm font-medium text-[#6B7280] mb-1">Custom &lt;head&gt; Code</label>
              <textarea
                id="customHead"
                name="customHead"
                rows={4}
                value={pageContent.seo.customHead}
                onChange={handleSeoChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow font-mono text-sm"
              />
            </div>
            <div className="flex items-center gap-3 pt-2">
                <input
                    type="checkbox"
                    id="noindex"
                    name="noindex"
                    checked={pageContent.seo.noindex || false}
                    onChange={handleSeoChange}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="noindex" className="text-sm font-medium text-[#6B7280]">
                    Discourage search engines from indexing this page (noindex)
                </label>
            </div>
          </div>
          <div className="mt-6 text-right">
            <button
              onClick={onSave}
              className="px-6 py-2 bg-[#10B981] hover:bg-[#34D399] text-white font-bold rounded-lg shadow-md transition-all duration-300"
            >
              Save Changes
            </button>
          </div>
        </>
      )}
    </div>
  );
};